import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loading } from '../components/Loading';

// LE-020 (S4-c): detalhe do evento — GET /api/events/:id
// Proprietário ou admin podem editar/excluir.

function formatDateLong(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' });
}

function formatTime(t) {
  if (!t) return null;
  return t.slice(0, 5);
}

export function EventDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEvent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/events/${id}`);
      setEvent(data);
    } catch (err) {
      const msg = err.response?.data?.error?.message;
      setError(msg || 'Evento não encontrado');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  const isOwner = user && event && event.user_id === user.id;
  const canEdit = user && (isOwner || user.role === 'admin');

  const handleDelete = async () => {
    if (!window.confirm('Remover este evento? Esta ação não pode ser desfeita.')) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${id}`);
      navigate(-1);
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Erro ao excluir');
      setDeleting(false);
    }
  };

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-boost) 0%, #5b21b6 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px',
      }}>
        <div className="nc-row">
          <button onClick={() => navigate(-1)} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <h1 className="nc-page-title">
              {loading ? 'Carregando...' : event?.name || 'Evento'}
            </h1>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => navigate(`/eventos/${id}/editar`)}
                style={{
                  background: 'rgba(255,255,255,0.2)', color: 'var(--nc-on-primary)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                }}
              >
                ✏️
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  background: 'rgba(255,255,255,0.2)', color: 'var(--nc-on-primary)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
                }}
              >
                {deleting ? '...' : '🗑️'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, textAlign: 'center',
          }}>
            {error}
          </div>
        ) : loading ? <Loading message="Carregando evento..." /> : event && (
          <>
            {/* Date + time */}
            <div style={{
              background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
              padding: 16, marginBottom: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 16 }}>📅</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)' }}>
                  {formatDateLong(event.date)}
                </span>
              </div>
              {(formatTime(event.time_start) || formatTime(event.time_end)) && (
                <div className="nc-body-sm" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <span>🕐</span>
                  <span>
                    {formatTime(event.time_start) || '—'}
                    {formatTime(event.time_end) ? ` até ${formatTime(event.time_end)}` : ''}
                  </span>
                </div>
              )}
              {event.address && (
                <div className="nc-body-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>📍</span>
                  <span>{event.address}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div style={{
                background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
                padding: 16, marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, color: 'var(--nc-text-dim)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </div>
              </div>
            )}

            {/* Image */}
            {event.image_url && (
              <div style={{
                borderRadius: 14, overflow: 'hidden', marginBottom: 14,
                background: 'var(--nc-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src={event.image_url} alt={event.name} style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }} />
              </div>
            )}

            {/* Meta */}
            <div style={{
              background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
              padding: 14,
            }}>
              {event.business_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                  <span>🏪</span>
                  <button
                    onClick={() => navigate(`/comercio/${event.business_id}`)}
                    style={{ background: 'none', border: 'none', color: 'var(--nc-boost)', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0 }}
                  >
                    {event.business_name}
                  </button>
                </div>
              )}
              {event.author_name && (
                <div className="nc-body-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>👤</span>
                  <span>Criado por {event.author_name}</span>
                </div>
              )}
              <div className="nc-body-sm" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span>🏙️</span>
                <span>{event.city_name} / {event.state}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
